/**
 * 제조신청 API 클라이언트 — backend /manufacture-requests/* 와 1:1.
 *
 * 권한:
 *   - 신청·조회 : 모든 로그인 사용자 (뷰어 포함 — 현장이 직접 올리는 게 목적)
 *   - 수정·삭제 : 본인 신청건 / 남의 건은 mgmt·admin. 승인된 건은 불가.
 *   - 승인·반려 : mgmt·admin (출고검수 승인자와 같은 범위)
 */

import { api } from './api';
import type {
  ManufactureApproveResult,
  ManufactureRequestInput,
  ManufactureRequestRow,
} from './types';

const BASE = '/manufacture-requests';

export async function listRequests(params?: {
  q?: string;
  status?: '신청' | '승인' | '반려';
}): Promise<ManufactureRequestRow[]> {
  const qs = new URLSearchParams();
  if (params?.q?.trim()) qs.set('q', params.q.trim());
  if (params?.status) qs.set('status', params.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`${BASE}${suffix}`);
}

/** 승인 대기 건수 — [제조승인] 버튼의 빨간 뱃지. */
export async function getPendingCount(): Promise<{ pending: number }> {
  return api.get(`${BASE}/pending-count`);
}

export async function getRequest(requestId: number): Promise<ManufactureRequestRow> {
  return api.get(`${BASE}/${requestId}`);
}

export async function createRequest(
  input: ManufactureRequestInput,
): Promise<{ requestId: number }> {
  return api.post(BASE, input);
}

/** 수정 — 반려된 신청을 고쳐서 저장하면 다시 '신청'(대기) 상태로 돌아간다. */
export async function updateRequest(
  requestId: number,
  input: ManufactureRequestInput,
): Promise<{ requestId: number }> {
  return api.patch(`${BASE}/${requestId}`, input);
}

export async function deleteRequest(requestId: number): Promise<{ requestId: number }> {
  return api.delete(`${BASE}/${requestId}`);
}

/**
 * 승인 = 실제 제조 실행 (원재료 차감 + 완제품 입고 + 원가 롤업).
 * patch 를 함께 보내면 승인자가 고친 내용으로 제조되고, 신청 기록도 그 내용으로 갱신된다.
 */
export async function approveRequest(
  requestId: number,
  patch?: ManufactureRequestInput,
): Promise<ManufactureApproveResult> {
  return api.post(`${BASE}/${requestId}/approve`, patch ? { patch } : {});
}

export async function rejectRequest(
  requestId: number,
  reason: string,
): Promise<{ requestId: number }> {
  return api.post(`${BASE}/${requestId}/reject`, { reason });
}
